import { getContentConfig } from "../api/requests/player.requests.js";
import { PhysicsMass } from "../physics/physics_masses.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { isConfigMissing } from "../utils/network_errors.js";

const CANNON_CONFIG_CACHE_KEY = "cannon_config_v1";

export interface CannonConfig
{
  ball_speed: number;
  ball_mass: number;
}

const DEFAULT_CONFIG: CannonConfig = {
  ball_speed: 90,
  ball_mass: 7.44,
};

function positiveNumber( value: unknown, fallback: number, max: number ): number
{
  const number = typeof value === "number" ? value : Number( value );
  return Number.isFinite( number ) && number > 0 && number <= max ? number : fallback;
}

function normalize( raw: unknown ): CannonConfig
{
  const value = raw && typeof raw === "object" && !Array.isArray( raw )
    ? raw as Record<string, unknown>
    : {};
  return {
    ball_speed: positiveNumber( value.ball_speed, DEFAULT_CONFIG.ball_speed, 200 ),
    ball_mass: positiveNumber( value.ball_mass, DEFAULT_CONFIG.ball_mass, 100 ),
  };
}

export class CannonConfigService
{
  static hasCachedConfig(): boolean
  {
    return PersistentCache.read<unknown>( CANNON_CONFIG_CACHE_KEY ) !== null;
  }

  private static config = CannonConfigService.restoreCached();
  private static refreshing: Promise<CannonConfig> | null = null;

  private static restoreCached(): CannonConfig
  {
    const raw = PersistentCache.read<unknown>( CANNON_CONFIG_CACHE_KEY );
    const config = normalize( raw );
    PhysicsMass.cannon_ball = config.ball_mass;
    return config;
  }

  public static get(): CannonConfig
  {
    return this.config;
  }

  private static apply( config: CannonConfig ): CannonConfig
  {
    this.config = config;
    // Retain the configured value in PhysicsMass for debug tools and any
    // legacy diagnostics that expose the cannon-ball base mass.
    PhysicsMass.cannon_ball = config.ball_mass;
    return config;
  }

  public static async refresh(): Promise<CannonConfig>
  {
    if ( this.refreshing ) return this.refreshing;

    this.refreshing = getContentConfig<unknown>( "cannon" )
      .then( config =>
      {
        PersistentCache.write( CANNON_CONFIG_CACHE_KEY, config );
        return this.apply( normalize( config ) );
      } )
      .catch( error =>
      {
        if ( isConfigMissing( error ) )
        {
          PersistentCache.remove( CANNON_CONFIG_CACHE_KEY );
          return this.apply( normalize( null ) );
        }
        console.warn( "[CannonConfig] failed to fetch config, keeping current", error );
        return this.config;
      } )
      .finally( () => { this.refreshing = null; } );
    return this.refreshing;
  }
}
